import { MDBBtn, MDBContainer, MDBIcon } from "mdbreact";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import images from "../../../assets/img/app";
import { AppContainer } from "../../../containers/App";
import { CampaignContainer } from "../../../containers/Campaign";
import { ErrorContainer } from "../../../containers/Error";
import { Recommend, StampCardContainer } from "../../../containers/StampCard";
import { UserContainer } from "../../../containers/User";
import StampCard from "../../modules/stamp-card/StampCard";
import TenantDescriptionModal from "../../modules/tenant-description-modal/TenantDescriptionModal";
import TenantSelectSlider from "../../modules/tenant-select-slider/TenantSelectSlider";
import TenantSelectCarousel from "../../modules/tenant-select-carousel/TenantSelectCarousel";
import classes from "./TenantSelectPage.module.scss";
import formatDate from "../../../utilities/DateFormatter";
import MediaQuery from "react-responsive";
import { Config } from "../../../config/Config";

/**
 * テナント選択画面コンポーネント
 */
const TenantSelectPage: React.FC = () => {
  /** テナント詳細情報モーダルの開閉状態管理 */
  const [isTenantDescriptionOpen, setIsTenantDescriptionOpen] = useState(false);

  const appContainer = AppContainer.useContainer();
  const errorContainer = ErrorContainer.useContainer();
  const userContainer = UserContainer.useContainer();
  const stampCardContainer = StampCardContainer.useContainer();
  const campaignContainer = CampaignContainer.useContainer();
  const history = useHistory();

  const user = userContainer.user;
  const userId = user.id || "";

  const stampCard = stampCardContainer.stampCard;
  const recommends = stampCard?.recommends;

  const campaign = campaignContainer.campaign;
  const campaignId = Config.campaignId || "";
  const categories = campaign?.categories;

  const dateFormat = "YYYY年MM月DD日(AA)";
  const campaignStartDate = formatDate(dateFormat, campaign?.from);

  useEffect(() => {
    if (!campaign) {
      // キャンペーンデータを初期化
      appContainer.setLoading(true);
      campaignContainer
        .initCampaign(campaignId)
        .then(() => appContainer.setLoading(false))
        .catch((error) => errorContainer.setDefaultError(userId, error));
    } else if (!stampCard) {
      appContainer.setLoading(true);
      stampCardContainer
        .recommend(
          userId,
          campaignId,
          categories?.map((c) => c.category) || [],
          user.token || ""
        )
        .then(() => appContainer.setLoading(false))
        .catch((error) => errorContainer.setDefaultError(userId, error));
    }

    // 初回のみ呼び出す処理のため、警告は無視する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  useEffect(() => {
    if (!user.token) {
      // 未ログインの場合、ログインページに遷移
      history.push("/login");
    }

    if (!stampCard) {
      return;
    }
  }, [history, stampCard, campaignId, user.token]);

  /** タップ（クリック）されたリコメンド情報を管理するための state */
  const [selectedRecommend, setSelectedRecommend] = useState<{
    category: string;
    recommend: Recommend;
  }>({
    category: "",
    recommend: {
      id: "",
      image: "",
      name: "",
    },
  });

  /**
   * テナント選択スタンプクリック
   * @param category カテゴリ
   * @param recommend リコメンドされたテナント
   */
  const handleTenantClick = (category: string, recommend: Recommend) => {
    if (!recommends) {
      return;
    }
    const targetRecommend = recommends[category].find(
      (r) => r.id === recommend.id
    ) || {
      id: "",
      image: "",
      name: "",
      stampType: "",
    };

    setSelectedRecommend({ category, recommend: targetRecommend });
    setIsTenantDescriptionOpen(true);
  };

  /**
   * 「お店を選択」押下時の処理
   */
  const handleRecommendSelect = (category: string, recommend: Recommend) => {
    stampCardContainer.recommendSelect(category, recommend);
  };

  /**
   * 決定ボタンクリック
   */
  const handleCommitClick = async () => {
    try {
      appContainer.setLoading(true);
      if (campaign && !stampCard?.isEntry) {
        // 未エントリーであれば、エントリー状態を更新
        const result = await stampCardContainer.entry(
          campaign.from,
          campaign.to
        );
        if (result === "preparing") {
          // キャンペーン開始前の場合
          errorContainer.setCustonError(
            "キャンペーン期間外",
            [
              `本キャンペーンは ${campaignStartDate}`,
              "より開始します。",
              "キャンペーンの開始までお待ちください。",
            ],
            userId
          );
          return;
        }

        if (result === "end") {
          // キャンペーン終了後の場合
          errorContainer.setCustonError(
            "キャンペーン期間外",
            [
              "本キャンペーンは終了しました。",
              "次回のキャンペーンをお待ちください。",
            ],
            userId
          );
          return;
        }
      }

      if (user.token) {
        await stampCardContainer.setStamps(user.token || "");
        history.push(`/stamp-card`);
      }
    } catch (error) {
      errorContainer.setDefaultError(userId, error);
    } finally {
      appContainer.setLoading(false);
    }
  };

  /**
   * スタンプ変更ボタンクリック
   */
  const handleReplaceClick = async () => {
    try {
      appContainer.setLoading(true);
      if (user.token) {
        await stampCardContainer.setStamps(user.token, false);
        history.push(`/stamp-card`);
      }
    } catch (error) {
      errorContainer.setDefaultError(userId, error);
    } finally {
      appContainer.setLoading(false);
    }
  };

  /**
   * 「さらに表示」ボタンクリック
   * @param category カテゴリ
   */
  const handleMorePanelClick = (category: string) => {
    // 対象カテゴリのテナント一覧画面を表示
    history.push(`/tenant-list/${category}`);
  };

  /**
   * スタンプ取り外しクリック
   * @param id スタンプID
   */
  const handleStampRemoveClick = (id: string) => {
    stampCardContainer.removeSelectedRecommend(id);
  };

  // 選択中スタンプの抽出
  // 未選択のものがあれば未選択スタンプで埋める
  const noSelectStamp = {
    id: "",
    image: images.noSelectStamp,
    name: "",
    area: "",
    isAchieve: false,
    isChecked: false,
  };
  const selectedRecommends = Array(4)
    .fill(0)
    .map((_, i) => {
      if (!stampCard) {
        return { category: "", recommend: noSelectStamp };
      }

      return stampCard.selectedRecommends.length > i
        ? stampCard.selectedRecommends[i]
        : { category: "", recommend: noSelectStamp };
    });

  return (
    <MDBContainer>
      <div className={`text-center my-3 py-3 ${classes.pageTitle}`}>
        スタンプシートに登録するショップをえらんでください
      </div>

      <div className="d-flex justify-content-center">
        <div className={`px-sm-4 ${classes.widthControl}`}>
          <StampCard
            title="選択中のショップ"
            stamps={
              selectedRecommends.map((s) => {
                return {
                  id: s.recommend.id,
                  image: s.recommend.image,
                  categoryName:
                    campaign?.categories.find((c) => c.category === s.category)
                      ?.label || "",
                  isAchieve: s.recommend.isAchieve || false,
                  isChecked: true,
                  isRemovable: !s.recommend.isAchieve,
                };
              }) || []
            }
            isCustomizable={false}
            onRemoveClick={(id) => handleStampRemoveClick(id)}
          ></StampCard>
        </div>
      </div>

      <div className="py-2">
        {categories?.map((c) => {
          return selectedRecommends.filter((sr) => sr.recommend.id).length <
            categories.length ? (
            <div key={c.category} className="mb-sm-4">
              {/* スライダーモード */}
              <MediaQuery query="(max-width: 576px)">
                <TenantSelectSlider
                  headerIcon={c.icon}
                  headerText={c.label}
                  tenants={
                    recommends
                      ? recommends[c.category].map((t) => {
                          return {
                            id: t.id,
                            name: t.name,
                            image: t.image,
                          };
                        })
                      : []
                  }
                  isSelected={selectedRecommends.some(
                    (sr) => sr.category === c.category
                  )}
                  onMorePanelClick={() => handleMorePanelClick(c.category)}
                  onStampClick={(recommend) =>
                    handleTenantClick(c.category, recommend)
                  }
                ></TenantSelectSlider>
              </MediaQuery>

              {/* カルーセルモード */}
              <MediaQuery query="(min-width: 576px)">
                <TenantSelectCarousel
                  headerIcon={c.icon}
                  headerText={c.label}
                  tenants={recommends ? recommends[c.category] : []}
                  isSelected={selectedRecommends.some(
                    (sr) => sr.category === c.category
                  )}
                  onMorePanelClick={() => handleMorePanelClick(c.category)}
                  onDetailOpenClick={(recommend) =>
                    handleTenantClick(c.category, recommend)
                  }
                  onRecommendSelect={(recommend) => {
                    setSelectedRecommend({
                      category: c.category,
                      recommend: recommend,
                    });
                    handleRecommendSelect(c.category, recommend);
                  }}
                ></TenantSelectCarousel>
              </MediaQuery>
            </div>
          ) : null;
        })}
      </div>

      <div className={`mt-3 mt-sm-4 d-flex justify-content-center`}>
        <div className={classes.hintMessage}>
          {selectedRecommends.filter((sr) => sr.recommend.id).length ===
          categories?.length ? (
            <React.Fragment>
              {stampCard?.isEntry ? (
                <div>
                  <ul className={`mt-2 my-sm-3 ${classes.list}`}>
                    <li>
                      ショップを選び直したい場合は、
                      <MDBIcon icon="times" className="mx-1" />
                      ボタンで選択を解除してください。
                    </li>
                  </ul>
                  <div
                    className={`pt-sm-5 text-center ${classes.buttonContainer}`}
                  >
                    <MDBBtn
                      className={`mt-2 ${classes.commitButton}`}
                      color="yellow"
                      onClick={handleReplaceClick}
                    >
                      ショップを変更する
                    </MDBBtn>
                  </div>
                </div>
              ) : (
                <React.Fragment>
                  <div className={`text-center ${classes.title}`}>
                    スタンプラリーを始めましょう！
                  </div>
                  <ul className={`mt-2 my-sm-3 ${classes.list}`}>
                    <li className="mt-2">
                      「これで決定！！」を押下すると、スタンプラリーを開始します。
                    </li>
                    <li className="mt-2">
                      ショップを選び直したい場合は、
                      <MDBIcon icon="times" className="mx-1" />
                      ボタンで選択を解除してください。
                    </li>
                  </ul>
                  <div
                    className={`pt-sm-5 text-center ${classes.buttonContainer}`}
                  >
                    <MDBBtn
                      className={classes.commitButton}
                      color="yellow"
                      onClick={handleCommitClick}
                    >
                      これで決定！！
                    </MDBBtn>
                  </div>
                </React.Fragment>
              )}
            </React.Fragment>
          ) : null}
          {stampCard?.isEntry ? (
            <div className={`text-center ${classes.return}`}>
              <MDBBtn
                className={`mt-2 ${classes.returnButton}`}
                color="yellow"
                onClick={() => {
                  history.push(`/stamp-card`);
                }}
              >
                スタンプシートへ戻る
              </MDBBtn>
            </div>
          ) : null}
        </div>
      </div>

      <TenantDescriptionModal
        onStampSelect={() => {
          handleRecommendSelect(
            selectedRecommend.category,
            selectedRecommend.recommend
          );
          setIsTenantDescriptionOpen(false);
        }}
        isOpenModal={isTenantDescriptionOpen}
        onCloseButtonClick={() => {
          setIsTenantDescriptionOpen(false);
        }}
        tenantDescription={{
          image:
            selectedRecommend.recommend.banner ||
            selectedRecommend.recommend.image,
          name: selectedRecommend.recommend.name,
          description: selectedRecommend.recommend.description || "",
          isRecommended: true,
          genre: selectedRecommend.recommend.genre,
          floor: selectedRecommend.recommend.area?.join("/"),
          businessHours: selectedRecommend.recommend.businessHours,
          tel: selectedRecommend.recommend.tel,
          budget: selectedRecommend.recommend.budget,
        }}
      />
    </MDBContainer>
  );
};

export default TenantSelectPage;
